import { useMemo, useRef } from 'react';

import type { QmsDisplayCounterPublic } from './types';

export interface DisplayCounterLive {
  currentDisplayCode: string | null;
  /** ISO timestamp of the latest call/recall for the current token, if any. */
  currentCalledAt: string | null;
  status: 'idle' | 'serving';
  /** Epoch ms of the last code change; 0 on first observation (no blink). */
  lastChangeAt: number;
}

export interface DisplayCallEntry {
  id: string;
  counterId: string;
  counterCode: string;
  counterName: string;
  displayCode: string;
  changedAt: number;
}

export interface DisplayLiveState {
  perCounter: Record<string, DisplayCounterLive>;
  calls: DisplayCallEntry[];
}

interface Tracked {
  /** Composite of display_code + called_at — a recall keeps the code but
   *  bumps called_at, so this string changes and we trigger a fresh blink. */
  key: string;
  changedAt: number;
}

function counterKey(counter: QmsDisplayCounterPublic): string {
  return `${counter.current_display_code ?? ''}|${counter.current_called_at ?? ''}`;
}

/**
 * Derives live board state from the polled snapshot. The board has no
 * WebSocket; instead this compares each poll's counter codes against the
 * previous poll so a freshly changed counter can blink for a short window.
 */
export function useDisplayLive(
  counters: QmsDisplayCounterPublic[],
): DisplayLiveState {
  const trackRef = useRef<Map<string, Tracked>>(new Map());

  return useMemo(() => {
    const track = trackRef.current;
    const now = Date.now();
    const perCounter: Record<string, DisplayCounterLive> = {};

    for (const counter of counters) {
      const code = counter.current_display_code;
      const key = counterKey(counter);
      const prev = track.get(counter.id);
      let changedAt: number;
      if (!prev) {
        changedAt = 0;
        track.set(counter.id, { key, changedAt });
      } else if (prev.key !== key) {
        changedAt = now;
        track.set(counter.id, { key, changedAt });
      } else {
        changedAt = prev.changedAt;
      }
      perCounter[counter.id] = {
        currentDisplayCode: code,
        currentCalledAt: counter.current_called_at,
        status: code ? 'serving' : 'idle',
        lastChangeAt: changedAt,
      };
    }

    const calls: DisplayCallEntry[] = counters
      .filter((counter) => counter.current_display_code)
      .map((counter) => ({
        // Include called_at so a recall produces a new entry id and the
        // announcer treats it as a fresh call.
        id: `${counter.id}:${counter.current_display_code}:${counter.current_called_at ?? ''}`,
        counterId: counter.id,
        counterCode: counter.code,
        counterName: counter.name,
        displayCode: counter.current_display_code as string,
        changedAt: perCounter[counter.id]?.lastChangeAt ?? 0,
      }))
      .sort((a, b) => b.changedAt - a.changedAt);

    return { perCounter, calls };
  }, [counters]);
}
